import React, { useState } from 'react';
import { InfoButton, HeadingSmall } from '@ajat/design-system/Configurator';
import InfoTooltip from '@ajat/design-system/InfoTooltip';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../../shared';

export const meta = {
  component: InfoButton,
  title: 'Info Button',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '3815:1665',
  description:
    'The 18px circular (?) that opens an explanation. On desktop it shows its own tooltip on hover; on touch it needs an onClick, because the hover tooltip never fires there — pair it with InfoTooltip so the hint is reachable on every device. HeadingSmall renders one for you.',
  props: [
    ['tooltip', 'string', '—', 'Hover tooltip text — desktop only, never shown on touch'],
    ['onClick', 'func', '—', 'Click handler; usually opens an InfoTooltip or a SidePanel'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'Hint that works on touch too',
      code: `import { InfoButton } from '@ajat/design-system/Configurator';
import InfoTooltip from '@ajat/design-system/InfoTooltip';

// tooltip alone is desktop-only — always give it an onClick as well
<InfoButton tooltip={addon.hint} onClick={() => setAttrInfo(addon)} />

<InfoTooltip
  isOpen={Boolean(attrInfo)}
  onClose={() => setAttrInfo(null)}
  ariaLabel={attrInfo?.title || ''}
>
  <div dangerouslySetInnerHTML={{ __html: attrInfo.html }} />
</InfoTooltip>`,
    },
    {
      title: 'Through HeadingSmall',
      code: `// HeadingSmall renders the (?) itself — you only wire the handler
<HeadingSmall
  title="Størrelse"
  selection="MEDIUM"
  onInfoClick={() => setSizeGuideOpen(true)}
/>

// Suppress it where there is nothing to explain
<HeadingSmall title="Farve" selection="SORT" showInfoButton={false} />`,
    },
  ],
};

export default function InfoButtonPage() {
  const [open, setOpen] = useState(false);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States" hint="hover the second one on a pointer device">
        <DemoRow align="center">
          <Demo caption="DEFAULT" center><InfoButton /></Demo>
          <Demo caption="WITH TOOLTIP" center><InfoButton tooltip="Navnetryk placeres på brystet" /></Demo>
          <Demo caption="WITH ONCLICK" center><InfoButton tooltip="Åbner en forklaring" onClick={() => setOpen(true)} /></Demo>
        </DemoRow>

        <InfoTooltip isOpen={open} onClose={() => setOpen(false)} ariaLabel="Navnetryk">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-small-l)' }}>
            <h3 className="typo-h4" style={{ color: 'var(--color-gray-10)' }}>Navnetryk</h3>
            <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
              Maks. 16 tegn. Vi bruger kun store bogstaver.
            </p>
          </div>
        </InfoTooltip>
      </DemoCard>

      <DemoCard title="Inside HeadingSmall" hint="the usual place you will meet it">
        <div className="max-w-xl flex flex-col gap-[var(--space-ui-medium-m)]">
          <HeadingSmall title="Størrelse" selection="MEDIUM" extraInfo="EU 38–40" price="+49,-" onInfoClick={() => setOpen(true)} />
          <HeadingSmall title="Farve" selection="SORT" showExtraInfo={false} showInfoButton={false} price="+0,-" />
        </div>
      </DemoCard>

      <DemoCard title="Touch behaviour" surface={false}>
        <p className="typo-body-sm" style={{ color: 'var(--color-gray-7)', maxWidth: '68ch' }}>
          The hover tooltip is gated behind{' '}
          <code style={{ color: 'var(--color-gray-10)' }}>
            @media (hover: hover) and (pointer: fine)
          </code>{' '}
          — deliberately, so it never sticks open after a tap. That makes{' '}
          <code style={{ color: 'var(--color-gray-10)' }}>tooltip</code> alone
          invisible on phones: give the button an{' '}
          <code style={{ color: 'var(--color-gray-10)' }}>onClick</code> whenever
          the hint matters.
        </p>
      </DemoCard>
    </ComponentPage>
  );
}
